<template>
    <div v-if="route.meta?.blankLayout" class="blank-layout-wrapper">
        <slot></slot>
    </div>
    <div v-else class="root-container n-border-box">
        <!-- 左侧导航栏 -->
        <nav
            aria-label="Main navigation">
            <app-sidebar
                :show-menu="showMenu"
                @toggle-menu="showMenu = !showMenu"
                v-if="shouldShowMenu" />
        </nav>

        <!-- 右侧内容区域 -->
        <div
            class="main-content n-flex n-flex-dr-c n-border-box n-flex-g-w-1-hack n-vh-1"
            :class="[
                { 'has-small-header': route.meta?.showSmallScreenHeader },
                { 'c-body-bg': ['chat', 'video'].includes(String(route.name)) },
                { 'is-user-home': route.name === 'user-home' }
            ]">
            <header
                class="main-header-for-small n-flex-a-c n-flex-j-sb n-col-1 n-border-box n-flex-s"
                v-if="route.meta?.showSmallScreenHeader">
                <router-link
                    :to="{ name: 'home' }"
                    class="logo-container n-flex n-flex-a-c n-c-4"
                    aria-label="Sovi.AI - Go to homepage">
                    <img
                        src="@/assets/images/logo.png"
                        class="n-block logo-img"
                        alt="Sovi.AI logo" />
                    <span class="logo-text n-f-bold7">{{ APP_NAME }}</span>
                </router-link>

                <icon-button
                    icon="menu"
                    type="secondary"
                    class="n-flex-s menu-icon n-pointer c-hover"
                    :size="55"
                    :icon-size="30"
                    aria-label="Toggle navigation menu"
                    @click="showMenu = !showMenu" />
            </header>

            <main class="main-body n-flex-g n-flex-g-h-1-hack n-scroll-y" role="main">
                <div
                    class="main-body-inner"
                    :class="{ 'with-footer': !shouldShowFooter }">
                    <slot></slot>
                </div>
                <seo-content-section
                    v-if="seoContentRoute"
                    :route="seoContentRoute" />
                <app-footer v-if="shouldShowFooter" />
            </main>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { APP_NAME } from '@/config/constant';
import { useRouter } from 'vue-router';

import AppSidebar from '@/components/business/app-sidebar.vue';
import AppFooter from '@/components/business/app-footer.vue';
// Lazy: the SEO section + its content map only load on the few routes that
// set `meta.seoContent`, keeping them out of the always-loaded layout chunk.
const SeoContentSection = defineAsyncComponent(() => import('@/components/common/SeoContentSection.vue'));

const store = useStore();
const route = useRoute();
const router = useRouter();
const isSmallScreen = computed(() => store.getters['viewport/isSmallScreen']);
const showMenu = ref(false);

// Visible SEO content block (rendered below the tool in the scroll container)
// for thin app routes that were "crawled - currently not indexed".
const seoContentRoute = computed(() => (route.meta?.seoContent as string) || '');

// 根据路由meta决定是否显示footer
const shouldShowFooter = computed(() => {
    return route.meta?.showFooter;
});

// 根据路由meta决定是否显示菜单
const shouldShowMenu = computed(() => {
    if (route.meta && typeof route.meta.showMenu === 'boolean') {
        return route.meta.showMenu;
    }
    // 桌面端路由冷启动时的同步路径匹配，完全避免路由未解析时导致侧边栏缺失产生的 CLS
    if (typeof window !== 'undefined') {
        let path = window.location.pathname;
        // 剥离多语言前缀（如 /zh-cn/, /de/, /es/ 等）以保证多语言路径下首屏侧边栏占位依然精准生效
        path = path.replace(/^\/(zh-cn|zh-tw|de|es|fr|ja|ko)\//i, '/');
        path = path.replace(/^\/(zh-cn|zh-tw|de|es|fr|ja|ko)$/i, '/');
        const menuPaths = ['/home', '/chat', '/study', '/apexam', '/video', '/expert', '/profile', '/history', '/testprep', '/questions', '/search', '/svip', '/message'];
        return menuPaths.some(p => path === p || path.startsWith(p + '/'));
    }
    return false;
});
</script>
<style scoped lang="scss">
.root-container {
    display: flex;
    height: 100vh;
    width: 100vw;
}

.logo-container {
    padding: 8px 12px;
    margin-bottom: 20px;
    text-decoration: none;

    .logo-img {
        width: 32px;
        height: 32px;
    }

    .logo-text {
        font-size: 24px;
        margin-left: 9px;
    }
}

.main-content {
    flex-grow: 1;

    .main-body {
        overflow-x: hidden !important;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .n-overflow-hidden {
            overflow-x: hidden;
        }
    }

    // 用户首页：渐变背景（底部双侧紫色光晕，向上渐隐至白）+ 内容区撑满可用高度
    &.is-user-home {
        background:
            radial-gradient(70% 55% at 8% 112%, rgba(140, 85, 242, 0.2) 0%, rgba(140, 85, 242, 0) 62%),
            radial-gradient(70% 55% at 92% 112%, rgba(140, 85, 242, 0.2) 0%, rgba(140, 85, 242, 0) 62%),
            #ffffff;

        .main-body-inner {
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    }
}

@media screen and (max-width: 768px) {
    .root-container {
        height: auto;

        .main-content.has-small-header {
            box-sizing: border-box;
            padding-top: 70px;
        }
    }
}
</style>
<style lang="scss" scoped>
.main-header-for-small {
    display: none;
    padding: 0 20px;
    height: 70px;
    background-color: var(--menu-background-color);
    border-bottom: 1px solid var(--menu-border-color);
    position: fixed;
    top: 0;
    z-index: 10;
    left: 0;

    .logo-container {
        padding: 0;
        margin: 0;
    }
}

@media screen and (max-width: 768px) {
    .main-header-for-small {
        display: flex;
    }
}
</style>
